"use client";

import React, { useState } from "react";
import Link from "next/link";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import Container from "../Container"; // Adjust path as needed

const faqs = [
  {
    question: "What do I get with Peerquik?",
    answer: (
      <>
        Peerquik is a complete Next.js boilerplate for building C2C/B2C marketplaces like Airbnb or eBay, saving you 1,000+ hours (~4+ months) of development. You get:
        <ul className="mt-2 space-y-1 text-gray-100">
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> A production-ready codebase with 22+ features, including Buyer/Seller authentication, Stripe payments, admin panel, and SEO tools.
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> TypeScript for clear, type-safe code (JavaScript optional).
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Next.js /app router for modern routing and performance (/pages router available).
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Comprehensive documentation for setup, customization, and deployment on Vercel or Coolify.
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Email support with the All-in plan for setup and customization.
          </li>
        </ul>
      </>
    ),
  },
  {
    question: "Why choose TypeScript over JavaScript?",
    answer: (
      <>
        Peerquik uses TypeScript by default for its clarity and robustness. TypeScript’s type safety catches errors early, speeds up development, and makes your codebase easier to maintain—perfect for complex marketplaces. JavaScript is available if you prefer a lighter setup.
      </>
    ),
  },
  {
    question: "Why use the /app router instead of /pages router?",
    answer: (
      <>
        The /app router, Peerquik’s default, leverages Next.js’s latest features like React Server Components, simplified routing, and improved performance. It’s ideal for modern, scalable applications. The /pages router is included for legacy projects or simpler needs.
      </>
    ),
  },
  {
    question: "Can I use Peerquik with a different tech stack?",
    answer: (
      <>
        Absolutely! Peerquik is built on React & Next.js, making it highly flexible. Swap Supabase for Firebase, Resend for SendGrid, or Stripe for Paddle. Use Postgres or another database with Prisma. Deploy on Vercel, Coolify, or any VPS—as long as you’re comfortable with React & Next.js, Peerquik adapts to your stack.
      </>
    ),
  },
  {
    question: "Is Peerquik just a website template?",
    answer: (
      <>
        Peerquik is much more than a template. It includes:
        <ul className="mt-2 space-y-1 text-gray-100">
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Copy/paste sections like pricing, FAQs, and responsive landing pages.
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Extensive UI components (modals, buttons, maps, forms with CAPTCHA).
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Business tools: Stripe payments with invoices, Resend emails, currency converter, cron jobs, and SEO optimization.
          </li>
          <li className="flex items-start">
            <span className="text-cyan-400 mr-2">✔</span> Admin panel for managing users, listings, and purchases.
          </li>
        </ul>
        <p className="text-gray-100 mt-2">
          Build a fully functional marketplace in days, not months.
        </p>
      </>
    ),
  },
  {
    question: "How is Peerquik better than other boilerplates?",
    answer: (
      <>
        Peerquik stands out with its focus on C2C/B2C marketplaces, offering 22+ pre-built features like dual-user authentication, Stripe subscriptions, and an admin panel. It saves 1,000+ hours (~4+ months) of development, includes TypeScript and /app router for modern development, and provides regular updates to keep your platform cutting-edge.
      </>
    ),
  },
  {
    question: "How often is Peerquik updated?",
    answer: (
      <>
        Peerquik is actively maintained with regular updates to enhance features, fix bugs, and support the latest Next.js and Stripe versions. Recent updates include improved Stripe webhooks and jsPDF invoice generation. Expect continuous improvements driven by community feedback.
      </>
    ),
  },
  {
    question: "Can I get a refund?",
    answer: (
      <>
        Once you buy Peerquik to get an email with a link to download the boilerplate, it’s yours forever, so refunds aren’t available. However, with 22+ features and comprehensive documentation, Peerquik enables you to launch a marketplace in days, delivering exceptional value.
      </>
    ),
  },
  {
    question: "Can I use PayPal?",
    answer: (
      <>
        Yes! You can send the EUR equivalent of the plan you want to purchase ($79 for Basic, $149 for Starter, $199 for All-in or $209 for Bundle) to our PayPal account:{" "}
        <a
          href="https://paypal.me/elianajordan"
          className="text-cyan-400 hover:underline"
        >
          paypal.me/elianajordan
        </a>
        . Once done, please email your GitHub username to{" "}
        <a
          href="mailto:peerquik@hotmail.com"
          className="text-cyan-400 hover:underline"
        >
          peerquik@hotmail.com
        </a>{" "}
        so we can send you the repo. Please allow a few hours for access.
      </>
    ),
  },
];

const FAQ = () => {
  const [openQuestionIndex, setOpenQuestionIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenQuestionIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="py-20 bg-gray-900 text-white">
      <Container>
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6 max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <div key={index}>
              <dt
                className={`text-lg font-semibold mb-2 flex justify-between items-center cursor-pointer ${openQuestionIndex === index ? "text-cyan-400" : "text-white"
                  }`}
                onClick={() => toggleQuestion(index)}
              >
                <span>{faq.question}</span>
                {openQuestionIndex === index ? (
                  <IoIosArrowUp size={20} />
                ) : (
                  <IoIosArrowDown size={20} />
                )}
              </dt>
              {openQuestionIndex === index && (
                <dd className="text-sm sm:text-md text-gray-100 leading-relaxed">
                  {faq.answer}
                </dd>
              )}
            </div>
          ))}
          <p className="text-center mt-8">
            Have another question? Contact me on {" "}
            <a
              href="https://twitter.com/eliana_jordan"
              className="text-cyan-400 hover:underline"
            >
              twitter
            </a>.
          </p>
        </div>
      </Container>
    </div>
  );
};

export default FAQ;